import "./TabCompanies.style.css";
import { useSuspenseQuery } from "@tanstack/react-query";
import { type FC, useCallback, useState } from "react";
import { type CompanyAllResponse, CompanyUserRole, roleToString } from "shared";
import { useEventCallback } from "usehooks-ts";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError } from "../common/error";
import { useModal } from "../common/modal/Modal";
import { SimpleTooltip } from "../common/SimpleTooltip";
import { useCompany, useCompanyActions } from "../company/useCompany";
import { Button } from "../components/Button";
import { ButtonIcon } from "../components/ButtonIcon";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { QueryKeys } from "../LinkGen";
import { ModalConfirmation } from "./ModalConfirmation";
import { ModalCreateCompany } from "./ModalCreateCompany";
import { ModalInviteUser } from "./ModalInviteUser";
import { ModalRenameCompany } from "./ModalRenameCompany";

export const TabCompanies: FC = () => {
	const { data: companies } = useSuspenseQuery({
		queryFn: async () => (await Api.GetCompanies()).data,
		queryKey: QueryKeys.Company.All(),
	});
	const { open, close } = useModal();

	const handleOpenCreateCompanyModal = useCallback(() => {
		open(ModalCreateCompany, {
			onSuccess: close,
		});
	}, [open, close]);

	return (
		<Card>
			<Card.Header>
				Companies
				<Button onClick={handleOpenCreateCompanyModal} icon="add_circle">
					Add company
				</Button>
			</Card.Header>
			<table className="table-companies">
				<tbody>
					{companies.map((company) => (
						<TableCompaniesRow key={company.id} company={company} />
					))}
				</tbody>
			</table>
		</Card>
	);
};

export const TableCompaniesRow: FC<
	Readonly<{
		company: CompanyAllResponse[number];
	}>
> = ({ company }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { deleteCompany } = useCompanyActions();
	const selectedCompany = useCompany();
	const { open, close } = useModal();
	const emit = useAlertEmit();

	const handleDelete = useEventCallback(() => {
		if (selectedCompany.id === company.id) {
			return emit({
				type: AlertType.ERROR,
				content:
					"Cannot delete selected company. Please select a different one before deleting.",
			});
		}

		open(ModalConfirmation, {
			confirmText: "Delete",
			icon: "delete",
			prompt: "Are you sure you want to delete this company?",
			onConfirm: async () => {
				await deleteCompany.mutateAsync(company.id);
				close();
			},
		});
	});

	const handleUserInvite = useEventCallback((companyID: number) => {
		open(ModalInviteUser, {
			companyId: companyID,
		});
	});

	const handleRename = useEventCallback(() => {
		open(ModalRenameCompany, {
			companyId: company.id,
			currentName: company.name ?? "",
		});
	});

	return (
		<>
			<tr>
				<td className="fw-bold" colSpan={3}>
					<div className="table-companies-name">
						{company.name || <i className="text-muted">Unnamed company</i>}
						<SimpleTooltip placement="top" tooltip="Rename company">
							<ButtonIcon
								icon="edit"
								aria-label="Rename company"
								onClick={handleRename}
							/>
						</SimpleTooltip>
					</div>
				</td>
				<td className="table-companies-actions">
					<div className="actions">
						<SimpleTooltip placement="top" tooltip="Invite User">
							<ButtonIcon
								icon="person_add"
								aria-label="Invite User"
								onClick={() => handleUserInvite(company.id)}
							/>
						</SimpleTooltip>

						<SimpleTooltip placement="top" tooltip="Delete">
							<ButtonIcon
								icon={"delete"}
								aria-label="Delete"
								onClick={handleDelete}
							/>
						</SimpleTooltip>

						<SimpleTooltip
							placement="top"
							tooltip={isExpanded ? "Hide users" : "Show users"}
						>
							<ButtonIcon
								icon={isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
								aria-label={isExpanded ? "Hide users" : "Show users"}
								onClick={() => setIsExpanded((prev) => !prev)}
							/>
						</SimpleTooltip>
					</div>
				</td>
			</tr>
			{isExpanded && <TableCompanyUsers company={company} />}
		</>
	);
};

export const TableCompanyUsers: FC<
	Readonly<{
		company: CompanyAllResponse[number];
	}>
> = ({ company }) => {
	const { open, close } = useModal();
	const { deleteCompanyUser, revokeInvite, updateUserRole } =
		useCompanyActions();
	const emit = useAlertEmit();
	const isOwner = company.myRole === CompanyUserRole.OWNER;

	const handleUserDelete = useEventCallback(
		(companyID: number, userID: number) => {
			open(ModalConfirmation, {
				confirmText: "Remove user",
				icon: "delete",
				prompt: "Are you sure you want to remove this user from the company?",
				onConfirm: async () => {
					await deleteCompanyUser
						.mutateAsync({ companyID, userID })
						.catch((e) => {
							emit({
								type: AlertType.ERROR,
								content: parseError(e) ?? "Unable to remove user",
							});
						});
					close();
				},
			});
		},
	);

	const revokeInviteHandle = useEventCallback(
		(companyID: number, inviteID: number) => {
			open(ModalConfirmation, {
				confirmText: "Revoke invite",
				icon: "delete",
				prompt: "Are you sure you want to revoke this invite?",
				onConfirm: async () => {
					await revokeInvite.mutateAsync({ companyID, inviteID }).catch((e) => {
						emit({
							type: AlertType.ERROR,
							content: parseError(e) ?? "Unable to revoke invite",
						});
					});
					close();
				},
			});
		},
	);

	const handleRoleChange = useEventCallback(
		async (userID: number, newRole: CompanyUserRole) => {
			await updateUserRole
				.mutateAsync({
					companyID: company.id,
					userID,
					role: newRole,
				})
				.catch((e) => {
					emit({
						type: AlertType.ERROR,
						content: parseError(e) ?? "Unable to update user role",
					});
				});
		},
	);

	return (
		<>
			<tr className="user-header">
				<td>First Name</td>
				<td>Last Name</td>
				<td>Email</td>
				<td>Role</td>
				<td />
			</tr>
			{company.users.map((user) => (
				<tr className="user" key={user.id}>
					<td>{user.firstName}</td>
					<td>{user.lastName}</td>
					<td>{user.email}</td>
					<td>
						{isOwner ? (
							<Select
								size="sm"
								value={user.role ?? CompanyUserRole.MEMBER}
								onChange={(e) =>
									handleRoleChange(user.id, e.target.value as CompanyUserRole)
								}
								aria-label={`Role for ${user.firstName} ${user.lastName}`}
							>
								{Object.values(CompanyUserRole).map((role) => (
									<option key={role} value={role}>
										{roleToString[role]}
									</option>
								))}
							</Select>
						) : (
							<span>{roleToString[user.role ?? CompanyUserRole.MEMBER]}</span>
						)}
					</td>
					<td className="table-companies-actions">
						<div className="actions">
							{user.role !== CompanyUserRole.OWNER && (
								<SimpleTooltip placement="top" tooltip="Remove user">
									<ButtonIcon
										icon="person_off"
										aria-label="Remove user"
										onClick={() => handleUserDelete(company.id, user.id)}
									/>
								</SimpleTooltip>
							)}
						</div>
					</td>
				</tr>
			))}
			{company.invites.map((invite) => (
				<tr className="user" key={invite.id}>
					<td>-</td>
					<td>-</td>
					<td>
						{invite.email}
						<i>&nbsp;[Invited]</i>
					</td>
					<td>
						<i className="text-muted">Pending</i>
					</td>
					<td className="table-companies-actions">
						<div className="actions">
							<SimpleTooltip placement="top" tooltip="Revoke invite">
								<ButtonIcon
									icon="person_off"
									aria-label="Revoke invite"
									onClick={() => revokeInviteHandle(company.id, invite.id)}
								/>
							</SimpleTooltip>
						</div>
					</td>
				</tr>
			))}
		</>
	);
};
